// SystemsCatalogAccordion.jsx
import Badge from '@/app/common/components/Badge'
import Label from '@/app/common/components/Label'
import { ProgramCategory } from '@/queries/program_catalog/schema'
import {
  SystemCatalogCategory,
  SystemCatalogItem,
} from '@/queries/system_catalog/system_catalog.schema'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shad/ui/accordion'
import { SystemCatalogItemStatuses } from '../../system_catalog/constants/system-catalog.constants'
import { commandScore } from '../../system_catalog/utils/system-catalog.utils'
import AddCategoryPopup from './_components/AddCategoryToProgramPopup'
import AddItemPopup from './_components/AddItemPopup'

interface SystemsCatalogAccordionProps {
  categories: SystemCatalogCategory[]
  programCategories: ProgramCategory[]
  items: SystemCatalogItem[]
  searchTerm: string
  statusFilter: SystemCatalogItemStatuses
  onAddCategorySuccess: () => void
}

function SystemsCatalogAccordion({
  categories,
  searchTerm,
  programCategories,
  statusFilter,
  onAddCategorySuccess,
}: SystemsCatalogAccordionProps) {
  const filteredCategories = categories
    .filter((category) => {
      if (statusFilter === 'All') {
        return category
      }
      return category.status === statusFilter
    })
    .map((item) => {
      const items = item.items.map((item) => item.name).join(', ')
      const categoryScore = commandScore(item.name + items, searchTerm)
      return {
        item,
        score: categoryScore,
      }
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item)

  function renderContent() {
    if (categories.length === 0) {
      return (
        <div className="text-gray-800 text-center text-sm py-10">
          There are no items or categories available in the system catalog yet.
        </div>
      )
    }

    // Filter categories based on search term and status filter

    function renderAddCategoryToProgram(category: SystemCatalogCategory) {
      const programCategory = programCategories.find(
        (programCategory) =>
          programCategory.global_item_category_id === category.id
      )

      if (!programCategory) {
        return (
          <AddCategoryPopup
            onAddCategorySuccess={onAddCategorySuccess}
            category={category}
          />
        )
      }
      if (
        programCategory?.status === 'Active' &&
        category.status === 'Active'
      ) {
        return <Badge semanticColor="Positive">In Program Catalog</Badge>
      }

      if (programCategory?.status === 'Inactive') {
        return (
          <Badge semanticColor="Notice">In Program Catalog - Inactive</Badge>
        )
      }

      return <Badge semanticColor="Negative">Inactive</Badge>
    }

    function renderAddItems(
      item: {
        name: string
        id: number
      },
      categoryId: number
    ) {
      const isInProgramCatalog = programCategories.some((item) => {
        return item.global_item_category_id === categoryId
      })

      if (!isInProgramCatalog) {
        return <></>
      }

      const currentCategory = programCategories.find(
        (programCategory) =>
          programCategory.global_item_category_id === categoryId
      )

      const currentItem = currentCategory?.program_items.find(
        (programItem) => programItem.global_item_id === item.id
      )

      const isItemInProgram = programCategories.some(
        (programCategory) =>
          programCategory.global_item_category_id === categoryId &&
          programCategory.program_items.some(
            (programItem) => programItem.global_item_id === item.id
          )
      )

      if (isItemInProgram && currentItem?.status === 'Inactive') {
        return (
          <Badge semanticColor="Notice">In Program Catalog - Inactive</Badge>
        )
      }
      if (isItemInProgram) {
        return <Badge semanticColor="Positive">In Program Catalog</Badge>
      }

      return (
        <AddItemPopup
          onAddItemSuccess={onAddCategorySuccess}
          itemName={item.name}
          itemCategoryId={categoryId}
          itemId={item.id}
        />
      )
    }

    if (filteredCategories.length === 0) {
      return (
        <div className="py-10 flex items-center justify-center text-gray-700">
          No items found
        </div>
      )
    }

    return (
      <Accordion
        type="multiple"
        className="border border-gray-200 p-4 rounded-md h-[60vh] max-h-[60vh] overflow-y-auto"
      >
        {filteredCategories.map((category) => {
          return (
            <AccordionItem key={category.id} value={category.name}>
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <AccordionTrigger>
                      <span className="font-medium text-gray-800">
                        {category.name}
                      </span>
                    </AccordionTrigger>
                    <span className="text-xs ml-4 text-gray-600">
                      {category.items.length} items
                    </span>
                  </div>
                  {renderAddCategoryToProgram(category)}
                </div>
              </div>
              <AccordionContent>
                {category.items.length > 0 ? (
                  <div className="">
                    {category.items.map((item) => {
                      return (
                        <div
                          key={item.id || item.name}
                          className="flex items-center justify-between gap-1 border-b last:border-b-0 border-gray-200 py-3 mx-6"
                        >
                          <Label weight={'normal'}>{item.name}</Label>
                          {renderAddItems(item, category.id)}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-gray-600">No items available</p>
                )}
              </AccordionContent>
            </AccordionItem>
          )
        })}
      </Accordion>
    )
  }

  const totalItems = filteredCategories.reduce((curr, acc) => {
    return acc.items.length + curr
  }, 0)
  return (
    <div className="ml-4">
      <div className="mb-2 mt-6 flex items-center justify-between">
        <div>
          <h3 className="font-medium text-gray-800">System Catalog</h3>
          <p className="text-gray-600 text-sm">
            Browse all available categories and items in the system.
          </p>
        </div>
        <div className="text-sm text-gray-600">
          {totalItems} items in {filteredCategories.length} categories
        </div>
      </div>
      {renderContent()}
    </div>
  )
}

export default SystemsCatalogAccordion
